import { Eye } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RecordPageShell } from '~/components/layout';
import ProviderKeyForm from '~/components/pages/notifications/provider-keys/ProviderKeyForm';
import { Alert, ConfirmModal, CopyButton, Modal, ModalActions } from '~/components/ui';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { apiErrorMessage, errorText, isNotFound } from '~/lib/apiError';
import {
  type ProviderKeyFormValues,
  providerKeyTitle,
  providerKeyToForm,
  revealedCredentialRows,
  toProviderKeyInput,
} from '~/lib/notification/providerKeyForm';
import {
  useDeleteProviderKeyMutation,
  useGetProviderKeyQuery,
  useRevealProviderKeyCredentialsMutation,
  useUpdateProviderKeyMutation,
} from '~/modules/query/notification/providerKeys';
import { routes } from '~/app/routes';

export default function ProviderKeyDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const keyQuery = useGetProviderKeyQuery(id ?? '', { skip: !id });
  const [updateProviderKey] = useUpdateProviderKeyMutation();
  const [deleteProviderKey, { isLoading: isDeleting }] = useDeleteProviderKeyMutation();
  const [revealCredentials, { isLoading: isRevealing }] = useRevealProviderKeyCredentialsMutation();

  const stored = keyQuery.currentData;
  const { draft, patch, dirty, markSaved } = useRecordDraft(stored, {
    id: id ?? '',
    version: stored?.updatedAt,
    recordId: (key) => key.id,
    toDraft: providerKeyToForm,
  });
  const guard = useUnsavedGuard(dirty);

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Plaintext lives here only while the modal is open.
  const [revealed, setRevealed] = useState<string | null>(null);

  const save = useRecordSave<ProviderKeyFormValues>({
    save: (form) =>
      updateProviderKey({ id: id ?? '', ...toProviderKeyInput(form, stored) }).unwrap(),
    success: 'Provider key updated',
    failure: 'The provider key could not be saved. Try again.',
  });

  const title = stored ? providerKeyTitle(stored) : 'Provider key';

  const remove = async () => {
    if (!id) return;
    try {
      await deleteProviderKey(id).unwrap();
      toast.success('Provider key deleted');
      guard.release();
      navigate(routes.notificationProviderKeys);
    } catch (error) {
      setConfirmDelete(false);
      setDeleteError(errorText(error, 'The provider key could not be deleted. Try again.'));
    }
  };

  const reveal = async () => {
    if (!id) return;
    try {
      setRevealed((await revealCredentials(id).unwrap()).credentials);
    } catch (error) {
      const status = (error as { status?: number } | null)?.status;
      toast.error(
        isNotFound(error)
          ? 'The saved credentials could not be read. Enter them again to replace them.'
          : status === 403
            ? 'Only a global admin can view provider credentials.'
            : errorText(error, 'The credentials could not be shown. Try again.'),
      );
    }
  };

  const loadFailed = !stored && keyQuery.isError;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Notifications' },
        { label: 'Provider keys', href: routes.notificationProviderKeys },
        { label: title },
      ]}
      actions={
        stored
          ? [
              ...(stored.hasCredentials
                ? [
                    {
                      label: 'Show credentials',
                      kind: 'secondary' as const,
                      onClick: reveal,
                      disabled: isRevealing,
                      startIcon: <Eye className="size-4" aria-hidden="true" />,
                      testId: 'provider-key-reveal-button',
                    },
                  ]
                : []),
              {
                label: 'Delete provider key',
                kind: 'danger',
                onClick: () => setConfirmDelete(true),
                disabled: save.busy || isDeleting,
                testId: 'provider-key-delete-button',
              },
            ]
          : undefined
      }
      loading={!draft && !keyQuery.isError}
      notFound={loadFailed && isNotFound(keyQuery.error)}
      notFoundTitle="Provider key not found"
      backTo={routes.notificationProviderKeys}
      backLabel="Back to provider keys"
      error={
        loadFailed && !isNotFound(keyQuery.error)
          ? { what: 'the provider key', message: apiErrorMessage(keyQuery.error) }
          : null
      }
      onRetry={() => keyQuery.refetch()}
      guard={guard}
      testId="provider-key"
    >
      {stored && draft && (
        <>
          {deleteError && <Alert variant="error" message={deleteError} />}

          <ProviderKeyForm
            draft={draft}
            patch={patch}
            stored={stored}
            dirty={dirty}
            busy={save.busy}
            error={save.error}
            onSubmit={async () => {
              // The refetch brings a new updatedAt that reseeds the draft, so the blanks stay blank.
              if (await save.submit(draft)) markSaved(draft, { ...draft, credentials: {} });
            }}
          />

          <Modal
            isOpen={revealed !== null}
            onClose={() => setRevealed(null)}
            size="small"
            title="Credentials"
            testId="provider-key-reveal-modal"
            footer={<ModalActions onDismiss={() => setRevealed(null)} />}
          >
            <dl className="space-y-4">
              {revealedCredentialRows(revealed ?? '').map(({ label, value }) => (
                <div key={label}>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-400">{label}</dt>
                  <dd className="flex items-start gap-2">
                    <span className="min-w-0 flex-1 break-all font-mono text-sm text-gray-800 dark:text-white/90">
                      {value}
                    </span>
                    <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} />
                  </dd>
                </div>
              ))}
            </dl>
          </Modal>

          <ConfirmModal
            isOpen={confirmDelete}
            title="Delete provider key"
            message={`Delete the ${title} key?`}
            confirmLabel="Delete provider key"
            variant="danger"
            dangerNote="Messages that rely on this key stop sending on this channel. This cannot be undone."
            testId="provider-key-delete"
            busy={isDeleting}
            onClose={() => setConfirmDelete(false)}
            onConfirm={remove}
          />
        </>
      )}
    </RecordPageShell>
  );
}
